import { useEffect, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { enablePush, disablePush, pushStatus, isStandalone } from "@/lib/push";
import { BellRing } from "lucide-react";

export function PushToggle() {
  const { user, isPro } = useAuth();
  const [status, setStatus] = useState("loading");
  const [busy, setBusy] = useState(false);

  useEffect(() => { pushStatus().then(setStatus); }, []);

  const toggle = async (on) => {
    setBusy(true);
    try {
      if (on) {
        await enablePush();
        setStatus("on");
        toast.success("Push alerts enabled on this device");
        api.post("/push/test").catch(() => {});
      } else {
        await disablePush();
        setStatus("off");
        toast("Push alerts turned off on this device");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
      setStatus(await pushStatus());
    } finally {
      setBusy(false);
    }
  };

  const ios = /iPhone|iPad/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const iosHint = ios && !isStandalone();
  const desc = iosHint
    ? "iPhone/iPad: open xauportal.com in Safari → Share → Add to Home Screen, then open that icon and enable here"
    : status === "unsupported" ? (ios ? "Needs iOS 16.4 or newer (Settings → General → Software Update)" : "This browser can't receive push alerts")
    : status === "denied" ? (ios ? "Blocked — iPhone Settings → Notifications → XAU Terminal → Allow" : "Blocked in browser settings — allow notifications for this site")
    : !user ? "Sign in to enable" : !isPro ? "Pro members only"
    : "Scalp, pre-news, post-news, position and price alerts, even when the app is closed";

  return (
    <div data-testid="push-toggle-row" className="flex items-center justify-between gap-3 pt-2 border-t border-white/10">
      <div className="min-w-0">
        <div className="text-sm font-medium flex items-center gap-1.5"><BellRing size={13} className="text-[#F59E0B]" /> Push alerts on this device</div>
        <div data-testid="push-toggle-desc" className="text-[11px] text-[#6B7280]">{desc}</div>
      </div>
      <Switch data-testid="toggle-push" checked={status === "on"} disabled={busy || status === "loading" || status === "unsupported" || status === "denied" || !user || !isPro}
        onCheckedChange={toggle} />
    </div>
  );
}
